import { BodyDTO, EmployeeDTO } from '@Models/model.types';
import { APIError, AsyncDropdownOptions, ListResponse, showActionLogMessage } from '@tlx/astro-shared';
import {
    Alert,
    AlertContent,
    Button,
    DateField,
    DropdownDrawer,
    DropdownField,
    DropdownOpener,
    DropdownScrollContainer,
    Form,
    Modal,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalLayout,
    ModalTitle,
    Option,
} from '@tlx/atlas';
import { SpinnerButton } from '@tlx/tsk-shared';
import { callApi, computeUrl, mapObjectToUrlSearchParams, MethodType } from '@Util/api.ts';
import { mapValidationMessages } from '@Util/apiErrorUtils.ts';
import { SanitizedHTMLAndMarkdown } from '@Util/SanitizedHTMLAndMarkdown.tsx';
import { APIUrls, DropdownUrls } from '@Util/urls.ts';
import React, { Dispatch, SetStateAction, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { SWRInfiniteKeyedMutator } from 'swr/infinite';

const formId = 'unionDeductionForm';

type UnionDeductionModalProps = {
    employeeIds: number[];
    open: boolean;
    onClose: () => void;
    mutate: SWRInfiniteKeyedMutator<ListResponse<EmployeeDTO>[]>;
    setValidationsMap: Dispatch<SetStateAction<Map<number, string[]>>>;
};
export const UnionDeductionModal: React.FC<UnionDeductionModalProps> = (props) => {
    const intl = useIntl();
    const [awaitingResponse, setAwaitingResponse] = useState(false);
    const [errorsToShowInModal, setErrorsToShowInModal] = useState<string[]>([]);
    const unexpectedErrorMessage = intl.formatMessage({
        id: 'validation_unexpected_error',
    });
    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();
        const formData = new FormData(event.currentTarget);
        const data = Object.fromEntries(formData.entries());
        props.setValidationsMap(new Map());
        const bodyDTO: BodyDTO = {
            employeeIds: props.employeeIds.join(','),
        };
        setAwaitingResponse(true);
        const { response, error } = await callApi(
            computeUrl(
                APIUrls.UNION_DEDUCTION,
                mapObjectToUrlSearchParams({
                    unionId: String(data.unionId),
                    startDate: String(data.startDate),
                }),
            ),
            MethodType.POST,
            JSON.stringify(bodyDTO),
        );
        setAwaitingResponse(false);
        if (response) {
            props.mutate();
            showActionLogMessage({
                pageName: intl.formatMessage({
                    id: 'heading_employee_overview',
                }),
                date: new Date(),
                message:
                    intl.formatMessage({
                        id: 'text_action_completed',
                    }) +
                    ' ' +
                    intl.formatMessage({ id: 'text_add_union_deductions' }),
            });
            props.onClose();
        }
        if (error) {
            if (error instanceof APIError) {
                mapValidationMessages(
                    error.details.validationMessages,
                    props.setValidationsMap,
                    setErrorsToShowInModal,
                    unexpectedErrorMessage,
                    props.onClose,
                );
            }
        }
    };
    return (
        <Modal
            open={props.open}
            onClose={() => {
                if (!awaitingResponse) {
                    props.onClose();
                }
            }}
        >
            <ModalLayout>
                <ModalCloseButton />
                <ModalTitle>
                    <FormattedMessage id={'text_add_union_deductions'} />
                </ModalTitle>
                <ModalContent>
                    {errorsToShowInModal.length > 0 && (
                        <Alert variant="error" className={'atl-mb-24'}>
                            <AlertContent>
                                <SanitizedHTMLAndMarkdown content={errorsToShowInModal.join('\n')} />
                            </AlertContent>
                        </Alert>
                    )}
                    <Form id={formId} className={'atl-w-full atl-flex atl-flex-col atl-gap-16'} onSubmit={onSubmit}>
                        <DropdownField
                            className={'atl-w-full'}
                            data-testid={'unionDeductionDropdown'}
                            isRequired
                            label={intl.formatMessage({ id: 'text_union' })}
                            name={'unionId'}
                            defaultSelectedKey={''}
                        >
                            <DropdownOpener />
                            <DropdownDrawer>
                                <DropdownScrollContainer>
                                    <Option value={''}>
                                        {intl.formatMessage({
                                            id: 'option_not_chosen',
                                        })}
                                    </Option>
                                    <AsyncDropdownOptions url={DropdownUrls.ACTIVE_UNIONS} />
                                </DropdownScrollContainer>
                            </DropdownDrawer>
                        </DropdownField>
                        <DateField
                            className={'atl-w-full'}
                            label={intl.formatMessage({
                                id: 'text_start_date',
                            })}
                            data-testid={'startDate'}
                            name={'startDate'}
                            isRequired
                        />
                    </Form>
                </ModalContent>
                <ModalFooter className="atl-flex atl-gap-8">
                    <SpinnerButton
                        className="atl-button--primary"
                        data-testid="end-employment-run-button"
                        type="submit"
                        form={formId}
                        disabled={awaitingResponse}
                        isLoading={awaitingResponse}
                    >
                        <FormattedMessage id="button_save" />
                    </SpinnerButton>

                    <Button
                        data-testid="cancel"
                        variant="secondary"
                        onClick={props.onClose}
                        disabled={awaitingResponse}
                    >
                        <FormattedMessage id="button_cancel" />
                    </Button>
                </ModalFooter>
            </ModalLayout>
        </Modal>
    );
};
